import { useEffect, useState } from "react";
import api, { API, apiError } from "@/lib/api";
import { PRIORITY_META } from "@/lib/meta";
import { fmtNZ } from "@/lib/datetime";
import { useStatuses } from "@/context/StatusContext";
import { formatDuration } from "@/components/JobTimer";
import { toast } from "sonner";
import { Download, BarChart3, Timer, CheckCircle2, Briefcase } from "lucide-react";

const fmtDateTime = (iso) =>
  fmtNZ(iso, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

function Metric({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-6" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">{label}</div>
        <Icon size={18} className={accent} />
      </div>
      <div className="font-heading text-3xl font-black tracking-tighter text-zinc-950 mt-3 tabular-nums">
        {value}
      </div>
    </div>
  );
}

export default function Reports() {
  const { metaFor, orderedKeys } = useStatuses();
  const [riggers, setRiggers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({
    status: "all",
    priority: "all",
    rigger_id: "all",
    location: "all",
    department: "all",
    start: "",
    end: "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const buildQuery = () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "all") p.append(k, v);
    });
    return p.toString();
  };

  const load = () => {
    setLoading(true);
    api
      .get(`/reports/jobs?${buildQuery()}`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(apiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/riggers").then((r) => setRiggers(r.data));
    api.get("/my-locations").then((r) => setLocations(r.data));
    api.get("/departments").then((r) => setDepartments(r.data));
    load();
    // eslint-disable-next-line
  }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/reports/jobs/export?${buildQuery()}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "jobs_report.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report exported");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">Analytics</div>
          <h1 className="font-heading text-4xl font-black tracking-tighter text-brand-display mt-1">
            Job Reports
          </h1>
        </div>
        <button
          onClick={exportCsv}
          data-testid="export-report-button"
          className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-5 py-2.5 flex items-center gap-2"
        >
          <Download size={18} /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
        <div>
          <label className="text-xs font-bold text-zinc-600 block mb-1">Status</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            data-testid="report-status-filter"
            className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <option value="all">All</option>
            {orderedKeys().map((s) => (
              <option key={s} value={s}>
                {metaFor(s).label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold text-zinc-600 block mb-1">Priority</label>
          <select
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            data-testid="report-priority-filter"
            className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <option value="all">All</option>
            {Object.keys(PRIORITY_META).map((p) => (
              <option key={p} value={p}>
                {PRIORITY_META[p].label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold text-zinc-600 block mb-1">Rigger</label>
          <select
            value={filters.rigger_id}
            onChange={(e) => setFilters({ ...filters, rigger_id: e.target.value })}
            data-testid="report-rigger-filter"
            className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <option value="all">All</option>
            {riggers.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold text-zinc-600 block mb-1">Location</label>
          <select
            value={filters.location}
            onChange={(e) => setFilters({ ...filters, location: e.target.value })}
            data-testid="report-location-filter"
            className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <option value="all">All</option>
            {locations.map((l) => (
              <option key={l.id} value={l.name}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold text-zinc-600 block mb-1">Department</label>
          <select
            value={filters.department}
            onChange={(e) => setFilters({ ...filters, department: e.target.value })}
            data-testid="report-department-filter"
            className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <option value="all">All</option>
            {departments.map((d) => (
              <option key={d.id} value={d.name}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold text-zinc-600 block mb-1">From</label>
          <input
            type="date"
            value={filters.start}
            onChange={(e) => setFilters({ ...filters, start: e.target.value })}
            data-testid="report-start-date"
            className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-zinc-600 block mb-1">To</label>
          <input
            type="date"
            value={filters.end}
            onChange={(e) => setFilters({ ...filters, end: e.target.value })}
            data-testid="report-end-date"
            className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          />
        </div>
        <div className="lg:col-span-7">
          <button
            onClick={load}
            data-testid="apply-report-filters"
            className="bg-zinc-950 text-white rounded-full font-bold tracking-tight hover:bg-zinc-800 transition-colors px-6 py-2 text-sm"
          >
            Apply filters
          </button>
        </div>
      </div>

      {loading || !data ? (
        <div className="text-zinc-400 font-mono text-sm p-8">Loading report…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <Metric label="Total Jobs" value={data.total_jobs} icon={Briefcase} accent="text-brand" testid="report-total" />
            <Metric label="Completed" value={data.by_status.processed_for_delivery} icon={CheckCircle2} accent="text-emerald-600" testid="report-completed" />
            <Metric
              label="Avg Time"
              value={data.avg_elapsed_seconds != null ? formatDuration(data.avg_elapsed_seconds) : "—"}
              icon={Timer}
              accent="text-purple-600"
              testid="report-avg-time"
            />
            <Metric
              label="Total Time"
              value={data.total_elapsed_seconds ? formatDuration(data.total_elapsed_seconds) : "—"}
              icon={BarChart3}
              accent="text-zinc-900"
              testid="report-timed"
            />
          </div>

          {/* Per-rigger */}
          <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] mb-6">
            <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
              <h2 className="font-heading text-lg font-bold tracking-tight text-zinc-900">
                Performance by Rigger
              </h2>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-left">
                  <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider">Rigger</th>
                  <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider text-right">Total</th>
                  <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider text-right">Completed</th>
                  <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider text-right">Avg Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {data.by_rigger.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-zinc-400">No data.</td></tr>
                ) : (
                  data.by_rigger.map((r) => (
                    <tr key={r.rigger} className="hover:bg-zinc-50">
                      <td className="px-4 py-2 font-semibold text-zinc-900">{r.rigger}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.total}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.completed}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.avg_elapsed_seconds != null ? formatDuration(r.avg_elapsed_seconds) : "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Jobs table */}
          <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] overflow-x-auto">
            <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
              <h2 className="font-heading text-lg font-bold tracking-tight text-zinc-900">
                Jobs ({data.jobs.length})
              </h2>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-left">
                  <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider">Job</th>
                  <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider">Customer</th>
                  <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider">Location</th>
                  <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider">Department</th>
                  <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider">Rigger</th>
                  <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider">Status</th>
                  <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider">Started</th>
                  <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider">Dispatched</th>
                  <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider text-right">Manual Time (hrs)</th>
                  <th className="px-4 py-2 font-bold text-zinc-600 uppercase text-xs tracking-wider text-right">Total Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {data.jobs.map((j) => (
                  <tr key={j.id} className="hover:bg-zinc-50">
                    <td className="px-4 py-2 font-semibold text-zinc-900">{j.title}</td>
                    <td className="px-4 py-2 text-zinc-600">{j.customer}</td>
                    <td className="px-4 py-2 text-zinc-600">{j.location || "—"}</td>
                    <td className="px-4 py-2 text-zinc-600">{j.department || "—"}</td>
                    <td className="px-4 py-2 text-zinc-600">{j.assigned_to_name || "—"}</td>
                    <td className="px-4 py-2">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-full ${metaFor(j.status).cls}`}>
                        {metaFor(j.status).label}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-zinc-600 tabular-nums text-xs">{fmtDateTime(j.started_at)}</td>
                    <td className="px-4 py-2 text-zinc-600 tabular-nums text-xs">{fmtDateTime(j.dispatch_at)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-zinc-700">{j.manual_time_hours != null ? j.manual_time_hours : "—"}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-zinc-700 font-semibold">
                      {j.timer_seconds != null ? formatDuration(j.timer_seconds) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
